/**
 * axios setup to use mock service
 */

import axios from 'axios';
import { observeFromResponse } from '@/utils/permissionsVersion';
import { redirectToLogin } from '@/utils/sessionRedirect';

const axiosServices = axios.create();
import { store, key } from '@/storeVuex';
import { addSentryBreadcrumb, captureApiError } from '@/plugins/monitoring';

interface ApiError extends Error {
    data?: any;
    status?: number;
    errors?: any;
    alert_message?: any;
}

function toApiError(data: any, status?: number): ApiError {
    if (data instanceof Error) return data as ApiError;
    if (data === null || data === undefined) {
        const err = new Error('API Error') as ApiError;
        if (status !== undefined) err.status = status;
        return err;
    }
    if (typeof data !== 'object') {
        const err = new Error(String(data)) as ApiError;
        if (status !== undefined) err.status = status;
        return err;
    }
    const message =
        data.message ||
        (typeof data.error === 'string' ? data.error : undefined) ||
        (data.alert_message && typeof data.alert_message === 'string' ? data.alert_message : undefined) ||
        'API Error';
    const err = new Error(message) as ApiError;
    err.data = data;
    if (status !== undefined) err.status = status;
    if (data.errors) err.errors = data.errors;
    if (data.alert_message) err.alert_message = data.alert_message;
    return err;
}

axiosServices.interceptors.request.use((config) => {
  const user: any = store.state.user;
  if(user){
    let token = user.token;
    config.headers['Authorization'] = "Bearer " + token;
  }
  addSentryBreadcrumb('api.axios', `${config.method?.toUpperCase()} ${config.url}`, {
    url: config.url,
    method: config.method,
  });
  return config;
});

// interceptor for http
axiosServices.interceptors.response.use(
    (response) => {
        // See fetch-wrapper: keep the cached permission map in step with the
        // server without ending the session.
        observeFromResponse(response);
        return response;
    },
    (error) => {
        // Check if error is IDLE_TIMEOUT
        const isIdleTimeout =
            error.response &&
            error.response.status === 401 &&
            error.response.data &&
            error.response.data.error_code === 'IDLE_TIMEOUT';

        if (isIdleTimeout) {
            // Clear all localStorage items
            // Clears storage and navigates AT MOST ONCE per document. Every
            // in-flight request 401s together when a token dies, and each one
            // used to assign location.href — restarting the navigation over and
            // over, which is the "login page keeps reloading" flicker.
            redirectToLogin();

            return Promise.reject(toApiError(error.response.data, error.response.status));
        }

        // Only a genuine authentication failure should end the session.
        // 401 = not authenticated (token missing/invalid/expired) -> log out.
        // 403 = authenticated but NOT permitted (Gate/role/permission denial, e.g. a
        // non-admin hitting an admin-only route). Conflating 403 with 401 wrongly
        // wiped the session and redirected to /login. Keep 403 out of the logout
        // path; only fall back to logout if the body explicitly says "Unauthenticated".
        const isUnauthenticated =
            (error.response && error.response.status === 401) ||
            (error.response && error.response.data &&
                (error.response.data.message?.includes('Unauthenticated') ||
                 error.response.data.error?.includes('Unauthenticated') ||
                 JSON.stringify(error.response.data).includes('Unauthenticated'))) ||
            (error.message && error.message.includes('Unauthenticated'));

        // Only the bsapi (Laravel) session can be "expired" — 401s from the
        // gateway, AI services, or any other origin must not nuke the user's
        // localStorage/sanctum session. Each non-bsapi caller has its own
        // error UI; we let those bubble up untouched.
        const requestUrl: string = error.config?.url || error.config?.baseURL || '';
        const gatewayUrl: string = (import.meta as any).env?.VITE_API_GATEWAY_URL || '';
        const isGatewayCall =
            (!!gatewayUrl && requestUrl.includes(gatewayUrl)) ||
            requestUrl.includes('localhost:3100') ||
            requestUrl.includes('127.0.0.1:3100') ||
            requestUrl.includes('gateway.builtsimple.com.au');

        if (isUnauthenticated && !isGatewayCall) {
            console.warn('⚠️ 401 Unauthorized - session expired');

            addSentryBreadcrumb('auth', 'Session expired - 401 from API', {
                url: error.config?.url,
                status: error.response?.status,
            }, 'error');

            // Show user a clear warning that their session expired
            // Use a global event so any page can react to it
            window.dispatchEvent(new CustomEvent('session-expired', {
                detail: { url: error.config?.url }
            }));

            // Clear auth and redirect to login. The latch is taken immediately
            // even though the navigation waits 2s for the toast, so the other
            // 401s arriving in the meantime cannot queue their own redirect.
            redirectToLogin(2000);
        }

        // Report API error to Sentry
        if (error.response) {
            captureApiError(
                error.config?.url || 'unknown',
                error.config?.method?.toUpperCase() || 'UNKNOWN',
                error.response.status,
                error.response.data,
                error.config?.data ? String(error.config.data).substring(0, 2000) : undefined
            );
        }

        return Promise.reject(
            toApiError(
                (error.response && error.response.data) || 'Wrong Services',
                error.response?.status,
            ),
        );
    }
);

declare module 'vue' {
    interface ComponentCustomProperties {
        $http: typeof axios;
        $translate: (key: string) => string;
    }
}

export default axiosServices;
